import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { getProfile } from "../../services/authService";
import { getKycStatus } from "../../services/kycService";
import { handleApiError } from "../../utils/handleApiError";
import api from "../../services/api";

const passwordSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, "Min. 8 characters required")
    .matches(/[A-Z]/, "At least one uppercase letter required")
    .matches(/[a-z]/, "At least one lowercase letter required")
    .matches(/[0-9]/, "At least one number required")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "At least one special character required")
    .required("New password is required"),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Please confirm your password"),
});

/* ── Icons ─────────────────────────────────────────────────── */
const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const EyeIcon = ({ show }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2">
    {show ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const DocIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9b9bc8" strokeWidth="1.5">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

/* ── Sub-components ─────────────────────────────────────────── */
const Field = ({ label, value, fullWidth = false }) => (
  <div className={fullWidth ? "col-span-2 " : ""}>
    <p className="text-[12px] font-semibold text-[#4C444E] uppercase tracking-wider mb-1.5">{label}</p>
    <p className="text-[16px] border-b border-[#E7E8E9] pb-2 font-bold text-[#34094D]">{value || <span className="text-[#CEC3D0]">—</span>}</p>
  </div>
);

const MerchantDocItem = ({ doc, isAvailable, docValue, needsReupload, onReupload }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  useEffect(() => {
    if (isAvailable && !previewUrl && !loading && docValue) {
      loadDocument();
    }
  }, [isAvailable, docValue]);

  const loadDocument = async () => {
    if (loading || previewUrl) return;
    setLoading(true);
    try {
      // docValue is expected to be the filename of the document
      // In case docValue is a full URL, we extract the last part
      let param = docValue;
      if (typeof docValue === 'string' && docValue.includes('/')) {
        param = docValue.split('/').pop();
      }

      const res = await api.get(`/kyc/document/${param}`, { responseType: "blob" });
      const mime = res.headers["content-type"] || "";
      const blob = new Blob([res.data], { type: mime });
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewType(mime);
    } catch (err) {
      console.error("Failed to load document", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl overflow-hidden text-center flex flex-col relative">
      {needsReupload && (
        <button
          onClick={onReupload}
          className="absolute top-1 right-1 bg-red-500 text-white text-[9px] px-2 py-0.5 rounded-sm cursor-pointer z-10 border-none transition-opacity hover:opacity-80"
          title="Reupload this document"
        >
          Reupload
        </button>
      )}
      <div
        className="h-16 bg-gradient-to-br from-indigo-50 to-purple-100 flex items-center justify-center cursor-pointer"
        onClick={() => isAvailable && loadDocument()}
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-purple-600 border-t-white rounded-full animate-spin"></div>
        ) : (
          <DocIcon />
        )}
      </div>
      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide pt-2 pb-0.5 px-1">{doc.label}</p>
      <div className="py-2">
        {isAvailable ? (
          !previewUrl ? (
            <button
              onClick={(e) => { e.preventDefault(); loadDocument(); }}
              disabled={loading}
              className="text-[10px] font-semibold text-emerald-600 hover:text-emerald-700 bg-transparent border-none cursor-pointer p-0 m-0"
            >
              {loading ? "Loading..." : "View Document"}
            </button>
          ) : (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] font-semibold text-emerald-600 hover:text-emerald-700"
            >
              Open Full Document
            </a>
          )
        ) : (
          <span className="text-[10px] font-semibold text-red-500">Not Uploaded</span>
        )}
      </div>
      {previewUrl && (
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-white border-t border-gray-100 p-2 flex items-center justify-center cursor-pointer block"
        >
          {previewType?.startsWith("image") ? (
            <img src={previewUrl} alt={doc.label} className="max-w-full h-auto rounded-lg shadow-sm mx-auto" style={{ maxHeight: "150px" }} />
          ) : previewType?.includes("pdf") ? (
            <iframe src={previewUrl} title={doc.label} className="w-full rounded-lg shadow-sm" style={{ height: "150px", border: "none" }} />
          ) : (
            <span className="text-xs text-gray-400">Preview not available</span>
          )}
        </a>
      )}
    </div>
  );
};

const CardHeader = ({ children }) => (
  <div className="px-5 py-4 flex items-center justify-between border-b border-[#F0EBF4]">
    {children}
  </div>
);

const MerchantStatusCard = ({ user, kycStatus }) => (
  <div className="bg-white rounded-[24px] shadow-[0px_12px_24px_0px_#4B23640A] p-5 shrink-0 w-64">
    <div className="flex items-center gap-2 mb-4">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#34094D" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
      <span className="text-[16px] font-bold text-[#34094D]">Merchant Status</span>
    </div>
    <div className="bg-[#F3F4F5] rounded-[16px] p-3.5">
      <p className="text-[10px] font-semibold text-[#4C444E] uppercase tracking-wider mb-2">KYC Status</p>
      <div className="flex items-center justify-between gap-1.5">
        <span className={`text-[15px] font-extrabold capitalize ${
          kycStatus === "verified" ? "text-[#16A34A]" :
          kycStatus === "rejected" ? "text-[#EF4444]" : "text-[#f5a524]"
        }`}>{kycStatus || "Pending"}</span>
        {kycStatus === "verified" && (
          <div className="w-5 h-5 rounded-full bg-[#16A34A] flex items-center justify-center">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
        )}
      </div>
    </div>
  </div>
);

const DomainCard = () => {
  const [showModal, setShowModal] = useState(false);
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [domainList, setDomainList] = useState([]);
  const [domainLoading, setDomainLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const fetchDomains = async () => {
    setDomainLoading(true);
    try {
      const res = await api.get('/domains');
      setDomainList(res.data?.data || res.data || []);
    } catch { setDomainList([]); }
    finally { setDomainLoading(false); }
  };

  useEffect(() => { fetchDomains(); }, []);

  const openAdd  = () => { setEditingId(null); setDomain(''); setDomainError(''); setShowModal(true); };
  const openEdit = (d) => { setEditingId(d.domain_id || d.id); setDomain(d.domain); setDomainError(''); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setDomain(''); setDomainError(''); setEditingId(null); };

  const handleSubmit = async () => {
    const trimmed = domain.trim();
    if (!trimmed) { setDomainError('Domain is required'); return; }
    setDomainError('');
    setSubmitting(true);
    try {
      if (editingId) {
        await api.post(`/domains/${editingId}`, { domain: trimmed });
        toast.success('Domain updated successfully');
      } else {
        await api.post('/domains', { domain: trimmed });
        toast.success('Domain update request submitted');
      }
      closeModal();
      fetchDomains();
    } catch (err) {
      setDomainError(err.response?.data?.message || 'Failed to submit domain request');
    } finally { setSubmitting(false); }
  };

  const latestDomain = domainList[0] || null;
  const statusCfg = {
    approved: { label: 'VERIFIED DOMAIN',             bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-400', text: 'text-emerald-700' },
    rejected: { label: 'REJECTED DOMAIN',             bg: 'bg-red-50',     border: 'border-red-200',     dot: 'bg-red-400',     text: 'text-red-700' },
    pending:  { label: 'VERIFICATION PENDING DOMAIN', bg: 'bg-amber-50',   border: 'border-amber-200',   dot: 'bg-amber-400',   text: 'text-amber-700' },
  };
  const cfg = statusCfg[latestDomain?.status] || statusCfg.pending;

  return (
  <>
    <div className="bg-white rounded-[24px] shadow-[0px_12px_24px_0px_#4B23640A] p-5 shrink-0 w-64">
      <div className="flex items-center gap-2 mb-4">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#34094D" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" strokeLinecap="round" />
        </svg>
        <span className="text-[16px] font-bold text-[#34094D]">Domain</span>
      </div>

      {domainLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="w-5 h-5 border-2 border-[#4B2364] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : latestDomain ? (
        <div className={`rounded-[14px] border p-3 mb-3 ${cfg.bg} ${cfg.border}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
              <span className={`text-[9px] font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.label}</span>
            </div>
            {(latestDomain.status === 'pending' || latestDomain.status === 'rejected') && (
              <button onClick={() => openEdit(latestDomain)}
                className="text-[10px] font-bold text-[#4B2364] bg-white px-2 py-0.5 rounded-md cursor-pointer hover:bg-[#4B2364]/5 transition border border-[#4B2364]/20">
                Edit
              </button>
            )}
          </div>
          <p className="text-[13px] font-bold text-[#34094D] break-all">{latestDomain.domain}</p>
          {latestDomain.created_at && (
            <p className="text-[10px] text-[#4C444E] mt-1">
              Requested on {new Date(latestDomain.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          )}
        </div>
      ) : (
        <p className="text-[12px] text-[#9B8FA0] text-center py-2 mb-2">No domain added</p>
      )}

      <button onClick={openAdd}
        className="w-full flex items-center justify-center gap-1.5 text-[12px] font-bold text-[#4B2364] bg-[#4B2364]/10 hover:bg-[#4B2364]/20 py-2.5 rounded-[12px] border-none cursor-pointer transition">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" /></svg>
        Add Domain
      </button>
    </div>

    {showModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden">
          <div className="bg-[linear-gradient(104.41deg,#754F89_0%,#4B2364_100%)] px-6 py-5 flex items-start justify-between">
            <div>
              <h2 className="text-[24px] font-bold text-white">{editingId ? 'Edit Domain' : 'Verify Domain'}</h2>
              <p className="text-[#F3E8FFB2] text-[14px] mt-0.5">Verify your domain</p>
            </div>
            <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 border-none cursor-pointer transition">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-[12px] font-bold text-[#4C444E] uppercase tracking-wider mb-1.5">Your Domain</label>
              <input type="text" value={domain}
                onChange={(e) => { setDomain(e.target.value); setDomainError(''); }}
                placeholder="https://yourserver.com/webhook/krestapay"
                className={`w-full bg-[#F3F4F5] rounded-[12px] px-4 py-3 text-[14px] text-[#191C1D] outline-none transition-all placeholder:text-[#9B8FA0] ${
                  domainError ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-[#4B2364]/30'
                }`}
              />
              {domainError && <p className="text-[12px] text-red-500 mt-1 font-medium">{domainError}</p>}
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={closeModal}
                className="flex-1 py-3 rounded-[12px] bg-[#F3F4F5] text-[#4C444E] text-[14px] font-semibold border-none cursor-pointer hover:bg-[#E7E8E9] transition">
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={submitting || !domain.trim()}
                className="flex-1 py-3 rounded-[12px] bg-[linear-gradient(104.8deg,#EA8534_0%,#A0111A_100%)] text-white text-[14px] font-bold border-none cursor-pointer hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
};
export default function MerchantProfile() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("account");
  const [showPwd, setShowPwd] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Data State
  const [user, setUser] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const docItems = [
    { label: "PAN CARD",        apiField: "pan_file" },
    { label: "AADHAAR FRONT",   apiField: "aadhaar_front" },
    { label: "AADHAAR BACK",    apiField: "aadhaar_back" },
    { label: "GST CERTIFICATE", apiField: "gst_certificate" },
    { label: "SELFIE VIDEO",    apiField: "selfie_video" },
  ];

  // Form State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch Profile Data on Mount
  useEffect(() => {
    fetchProfileData();
    fetchProfileImage();
    fetchKycData();
  }, []);

  const fetchKycData = async () => {
    try {
      const res = await getKycStatus();
      const data = res.data?.data || res.data;
      setKyc(data);
      setKycStatus(data?.kyc_status);
    } catch (err) {
      console.error("KYC fetch error", err);
    }
  };

  const fetchProfileImage = async () => {
    try {
      const res = await api.get("/auth/profile-image");
      const path = res.data?.data?.image_url;
      if (path) {
        const base = (process.env.REACT_APP_API_BASE_URL || "https://partner.krestapay.co.in/api")
          .replace(/\/api$/, "");
        setProfilePhoto(`${base}${path}`);
      }
    } catch {
      // no image — fallback to initials avatar
    }
  };

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const res = await getProfile();
      const userData = res.data?.data?.user || res.data?.data || res.data;
      setUser(userData);
      setKyc(userData?.kyc);
      setKycStatus(userData?.kyc_status || userData?.kyc?.status);
      const imgUrl = userData?.image || userData?.profile_image || userData?.avatar || null;
      if (imgUrl) setProfilePhoto(imgUrl);
      setError(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Update Password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    try {
      await passwordSchema.validate({ currentPassword, newPassword, confirmPassword }, { abortEarly: false });
    } catch (err) {
      toast.error(err.inner?.[0]?.message || err.message);
      return;
    }

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("current_password", currentPassword);
      fd.append("password", newPassword);
      fd.append("password_confirmation", confirmPassword);
      await api.post("/auth/update-password", fd);
      toast.success("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Upload Profile Photo
  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("image", file);
      await api.post("/auth/update-image", fd);
      toast.success("Photo updated successfully");
      await fetchProfileImage();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-800"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-8 py-6">
      <div className="w-full space-y-8">

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Header Card */}
            <div className="relative bg-white rounded-[24px] shadow-[0px_24px_48px_0px_#4B23640F] px-6 py-5 overflow-hidden">
              {/* Right-side 5% opacity purple gradient decoration */}
              <div
                className="absolute top-0 right-0 w-[50%] h-full"
                style={{
                  background: "linear-gradient(135deg, #754F89 0%, #4B2364 100%)",
                  clipPath: "polygon(30% 0%, 100% 0%, 100% 100%, 10% 100%)",
                  opacity: 0.07
                }}
              />
              <div className="relative z-10 flex items-end justify-between gap-4">

                {/* Left: Avatar + Info */}
                <div className="flex items-center gap-5">

                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <div className="w-[72px] h-[72px] rounded-[18px] bg-gradient-to-br from-[#754F89] to-[#4B2364] flex items-center justify-center text-white text-2xl font-bold overflow-hidden shadow-[0px_8px_16px_rgba(75,35,100,0.25)]">
                      {profilePhoto ? (
                        <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span>{user?.name?.substring(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    {/* <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${kycStatus === "verified" ? "bg-[linear-gradient(135deg,#EA8534_0%,#A0111A_100%)]" : "bg-amber-400"
                      }`} /> */}
                  </div>

                  {/* Info */}
                  <div>
                    {/* Pill label */}
                    <span className="inline-block text-[10px] font-bold text-[#754F89] bg-[#754F891A] px-3 py-1 rounded-full uppercase tracking-wider mb-1.5">
                      {user?.role === 0 || user?.role === "0" ? "Primary Merchant" : user?.role || "Merchant"}
                    </span>

                    {/* Name */}
                    <h1 className="text-[36px] font-extrabold text-[#34094D] leading-tight mb-2">
                      {user?.name}
                    </h1>

                    {/* Contact row */}
                    <div className="flex items-center flex-wrap gap-4 w-md">
                      {/* Email */}
                      <div className="flex items-center gap-1.5">
                        <svg width="20" height="16" viewBox="0 0 24 24" fill="none" stroke="#754F89" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        <span className="text-[16px] text-[#4C444E] font-medium">{user?.email || ""}</span>
                      </div>

                      {/* Phone */}
                      <div className="flex items-center gap-1.5">
                        <svg width="20" height="16" viewBox="0 0 24 24" fill="none" stroke="#754F89" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013 6.18 2 2 0 015 4h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L9.91 11a16 16 0 006 6l.32-.32a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                        </svg>
                        <span className="text-[16px] text-[#4C444E] font-medium">{user?.mobile}</span>
                      </div>
                    </div>
                    {/* Company
                    <div className="flex items-center gap-1.5">
                      <svg width="20" height="16" viewBox="0 0 24 24" fill="none" stroke="#7D747F" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                      <span className="text-[16px] text-[#4C444E] font-medium">{user?.company_name}</span>
                    </div> */}
                  </div>
                </div>

                {/* Right: Edit button */}
                <button
                  onClick={() => setTab("profile")}
                  className="flex items-center gap-2 text-[13px] font-semibold text-[#34094D] bg-[#E7E8E9] hover:bg-[#E7E8E9] border-none px-4 py-2.5 rounded-[16px] cursor-pointer transition-colors shrink-0"
                >
                  <EditIcon />
                  Edit Profile
                </button>
              </div>{/* end relative z-10 */}
            </div>{/* end Header Card */}



            {/* Account Information - always shown */}
            {tab === "account" && (
              <>
                <div className="flex gap-6 items-start">
                <div className="flex flex-col gap-4 shrink-0 w-64">
                  <MerchantStatusCard user={user} kycStatus={kycStatus} />
                  <DomainCard />
                </div>

                  {/* Account Information */}
                  <div className="bg-white rounded-[25px] shadow-[0px_8px_24px_0px_#4B23640A] flex-1 overflow-hidden">
                    <div className="px-6 py-5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="7" width="18" height="13" rx="2.5" stroke="#3B0A45" stroke-width="2" />
                          <rect x="9" y="3" width="6" height="6" rx="1.5" stroke="#3B0A45" stroke-width="2" />
                          <circle cx="9" cy="12" r="1.5" fill="#3B0A45" />
                          <path d="M6.5 16C6.5 14.9 7.6 14 9 14C10.4 14 11.5 14.9 11.5 16" fill="#3B0A45" />
                          <line x1="13" y1="11" x2="18" y2="11" stroke="#3B0A45" stroke-width="2" stroke-linecap="round" />
                          <line x1="13" y1="14" x2="18" y2="14" stroke="#3B0A45" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        <span className="text-[20px] font-bold text-[#34094D]">Account Information</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-[#BB8DD5] bg-[#4B2364] px-2.5 py-1 rounded-full uppercase tracking-wider">API Partner</span>
                      </div>
                    </div>
                    <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-5">
                      <Field label="Full Legal Name"   value={kyc?.personal?.full_name  || user?.name} />
                      <Field label="Date of Birth"     value={kyc?.personal?.dob} />
                      <Field label="Business Name"     value={kyc?.personal?.business_name} />
                      <Field label="Business Type"     value={kyc?.personal?.business_type} />
                      <Field label="PAN Number"        value={kyc?.pan?.pan_number} />
                      <Field label="GST Number"        value={kyc?.gst?.gst_number} />
                      <Field label="Email Address"     value={user?.email} />
                      <Field label="Mobile Number"     value={user?.mobile} />
                      <Field label="Business Address"  value={kyc?.gst?.business_address} fullWidth />
                    </div>

                    {/* Verification Status Row */}
                    {/* <div className="px-5 pb-5 grid grid-cols-4 gap-3">
                      {[
                        { label: "Aadhaar",   ok: kyc?.aadhaar?.verified,  at: kyc?.aadhaar?.verified_at },
                        { label: "PAN",       ok: kyc?.pan?.verified,      at: kyc?.pan?.verified_at },
                        { label: "GST",       ok: kyc?.gst?.verified,      at: kyc?.gst?.verified_at },
                        { label: "Liveness",  ok: kyc?.liveness?.verified, at: kyc?.liveness?.verified_at },
                      ].map(({ label, ok, at }) => (
                        <div key={label} className={`rounded-[12px] px-3 py-2.5 border ${ ok ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200" }`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {ok
                              ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                              : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
                            }
                            <span className={`text-[12px] font-bold ${ ok ? "text-green-700" : "text-gray-400" }`}>{label}</span>
                          </div>
                          {ok && at && (
                            <p className="text-[10px] text-green-600">{new Date(at).toLocaleDateString("en-IN")}</p>
                          )}
                        </div>
                      ))}
                    </div> */}
                  </div>
                </div>

                {/* Verified Documents */}
                <div className="bg-white rounded-[20px] shadow-[0px_8px_24px_0px_#4B23640A] border border-[#F0EBF4] overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between border-b border-[#F0EBF4]">
                    <div className="flex items-center gap-2">
                      <div className=" flex items-center justify-center">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <span className="text-[14px] font-bold text-[#1A1A1A]">Verified Documents</span>
                    </div>
                    <button className="flex items-center gap-1.5 text-[11px] font-semibold text-[#4B2364] bg-[#4B2364]/10 hover:bg-[#4B2364]/20 px-3 py-1.5 rounded-lg border-none cursor-pointer transition-colors">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download All KYC
                    </button>
                  </div>
                  <div className="p-4 grid grid-cols-5 gap-3">
                    {docItems.map((doc) => {
                      const url = kyc?.documents?.[doc.apiField];
                      const reuploadReq = kyc?.reupload_request || kycStatus?.reupload_request;
                      const needsReupload = reuploadReq?.status === "pending" && reuploadReq?.documents?.includes(doc.apiField);
                      return <MerchantDocItem key={doc.apiField} doc={doc} isAvailable={Boolean(url)} docValue={doc.apiField} needsReupload={needsReupload} onReupload={() => navigate("/onboard")} />;
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Profile Settings - shown when tab === profile (Edit Profile button) */}
            {tab === "profile" && (
              <>
                <div className="flex gap-6 items-start">
                <div className="flex flex-col gap-4 shrink-0 w-64">
                  <MerchantStatusCard user={user} kycStatus={kycStatus} />
                  <DomainCard />
                </div>
                    {/* Profile Photo Card */}
                    <div className="bg-white rounded-[20px] shadow-[0px_12px_24px_0px_#4B23640A] ">
                      <div className="px-5 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setTab("account")}
                            className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#F3F4F5] hover:bg-[#E7E8E9] border-none cursor-pointer transition-colors">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4C444E" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                            </svg>
                          </button>
                          <span className="text-[20px] font-bold text-[#34094D]">Profile Settings</span>
                        </div>
                        <button className="text-[14px] font-bold text-[#754F89] px-3 py-1.5 rounded-lg border-none cursor-pointer transition-colors">
                          Save Changes
                        </button>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-5 mb-5">
                          <div className="relative shrink-0">
                            <div className="w-16 h-16 rounded-[18px] bg-gradient-to-br from-[#754F89] to-[#4B2364] flex items-center justify-center text-white text-xl font-bold overflow-hidden shadow-[0px_4px_12px_rgba(75,35,100,0.25)]">
                              {profilePhoto ? <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" /> : user?.name?.substring(0, 2).toUpperCase()}
                            </div>
                            {/* <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${kycStatus === "approved" ? "bg-emerald-400" : "bg-amber-400"}`} /> */}
                          </div>
                          <div className="flex-1">
                            <p className="text-[16px] font-bold text-[#34094D] mb-0.5">{user?.name}</p>
                            <p className="text-[12px] text-[#4C444E] mb-3">Recommended: 400×400px • PNG, JPG or GIF (max. 1MB)</p>
                            <div className="flex gap-2">
                              <label className="text-[12px] font-semibold text-[#34094D] hover:bg-[#E7E8E9] border border-[#CEC3D0] rounded-[10px] px-4 py-2 cursor-pointer transition-colors">
                                <input type="file" accept="image/*" onChange={handleUploadPhoto} disabled={submitting} className="hidden" />
                                {submitting ? "Uploading..." : "Upload Now"}
                              </label>
                              <button onClick={() => setProfilePhoto(null)} disabled={submitting || !profilePhoto}
                                className="text-[12px] font-semibold text-[#BA1A1A] border border-[#CEC3D0] hover:bg-red-100 rounded-[10px] px-4 py-2 border-none cursor-pointer transition-colors disabled:opacity-40">
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Change Password Card */}
                    {/* Security / Change Password */}
                    <div className="bg-white rounded-[20px] shadow-[0px_12px_24px_0px_#4B23640A] mt-6 overflow-hidden">
                      <div className="px-5 py-4 flex justify-between items-center gap-2">
                        <span className="text-[20px] font-bold text-[#34094D]">Security / Change Password</span>
                        <div className="w-7 h-7 rounded-[10px] bg-[#4B2364]/10 flex items-center justify-center">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          </svg>
                        </div>
                      </div>
                      <form onSubmit={handleUpdatePassword} className="p-5 space-y-4">
                        <div>
                          <label className="block text-[10px] font-bold text-[#4C444E] uppercase tracking-wider mb-1.5">Current Password</label>
                          <div className="relative">
                            <input type={showPwd ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password"
                              className="w-full bg-[#F3F4F5] rounded-[12px] px-4 py-3 text-[16px] text-[#191C1D] outline-none focus:ring-2 focus:ring-[#4B2364]/30 pr-11 placeholder:text-[#7D747F99] transition-all" />
                            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3.5 top-1/2 -translate-y-1/2 border-none bg-transparent cursor-pointer p-0">
                              <EyeIcon show={showPwd} />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[10px] font-bold text-[#4C444E] uppercase tracking-wider mb-1.5">New Password</label>
                            <div className="relative">
                              <input type={showNew ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min. 8 characters"
                                className="w-full bg-[#F3F4F5] rounded-[12px] px-4 py-3 text-[16px] text-[#191C1D] outline-none focus:ring-2 focus:ring-[#4B2364]/30 pr-11 placeholder:text-[#7D747F99] transition-all" />
                              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3.5 top-1/2 -translate-y-1/2 border-none bg-transparent cursor-pointer p-0">
                                <EyeIcon show={showNew} />
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-[#4C444E] uppercase tracking-wider mb-1.5">Confirm New Password</label>
                            <div className="relative">
                              <input type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password"
                                className="w-full bg-[#F3F4F5] rounded-[12px] px-4 py-3 text-[16px] text-[#191C1D] outline-none focus:ring-2 focus:ring-[#4B2364]/30 pr-11 placeholder:text-[#7D747F99] transition-all" />
                              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 border-none bg-transparent cursor-pointer p-0">
                                <EyeIcon show={showConfirm} />
                              </button>
                            </div>
                          </div>
                        </div>
                        <button type="submit" disabled={submitting}
                          className="bg-[linear-gradient(104.8deg,#EA8534_0%,#A0111A_100%)] px-[28px] text-white font-bold text-[14px] py-3 rounded-[12px] hover:opacity-90 transition-all cursor-pointer border-none disabled:opacity-50 shadow-[0px_4px_12px_rgba(75,35,100,0.3)]">
                          {submitting ? "Updating..." : "Update Password"}
                        </button>
                      </form>
                    </div>
                  </div>
                
              </>
            )}

          </div>
    </div>
  );
}